import { useState, useRef, useEffect, forwardRef, Ref, useImperativeHandle, } from 'react';
import cx from 'classnames';
import { CommonIcon } from '@/components';
import handleToUserCenter from '@/utils/userCenter';
import errorCode from '@/constants/errorCode';
import { formatDisplayNumber } from '@/utils/formatNumber';
import Popover, { IPopoverRef } from '../Popover';
import { IUserLoginInfo } from '@/api/user';
import { PromiseState } from '@/constants';
import { USER_ACTION_LOTTIE_URL } from '@/constants/url';
import DotLottie, { IDotLottieMethods } from '@/components/DotLottie';
import Styles from './index.module.scss';


export enum ThreadDetailActionEnum {
  'like' = 'like',
  'collect' = 'collect',
  'comment' = 'comment',
  'share' = 'share',
  'extend' = 'extend'
}

export const ThreadDetailActions = [{
  key: ThreadDetailActionEnum.like,
}, {
  key: ThreadDetailActionEnum.collect,
}, {
  key: ThreadDetailActionEnum.comment,
}, {
  key: ThreadDetailActionEnum.share,
}];

interface IThreadDetailAction {
  tooltipText?: string;
  type: ThreadDetailActionEnum,
  defaultCount?: number;
  value?: number;
  isActiveDefault?: boolean;
  handleUserActionCb?: (isActive: boolean, visible?: boolean) => void;
  className?: string;
  title?: string;
  popoverNode?: React.ReactElement,
  isLogin?: boolean;
  account: IUserLoginInfo;
}

export type IThreadDetailActionRef = {
  hidePopover: (vis: boolean | undefined) => void;
  onShareCountAdd: () => void;
};

const ThreadDetailAction = ({
  popoverNode,
  tooltipText,
  type,
  handleUserActionCb,
  className,
  title,
  isActiveDefault = false,
  defaultCount = 0,
  value,
  isLogin = false,
  account,
}:IThreadDetailAction, ref: Ref<IThreadDetailActionRef>) => {
  const popoverRef = useRef<IPopoverRef>(null);
  const [count, setCount] = useState(value || defaultCount);
  const [isActive, setIsActive] = useState(isActiveDefault);
  const [isShowToolTip, setIsShowToolTip] = useState(false);
  const [showAnimation, setShowAnimation] = useState(false);
  const lottieRef = useRef<IDotLottieMethods>(null);

  useImperativeHandle(ref, () => ({
    hidePopover: (vis) => popoverRef.current?.hidePopover(vis),
    onShareCountAdd: () => setCount(count+1),
  }));

  useEffect(() => {
    if (typeof value === 'number') {
      setCount(value);
    }
  }, [value])

  /* 点赞收藏动画播放完成事件 */
  const onComplete = () => {
    setShowAnimation(false);
  };

  const handleUserAction = () => {
    const visible = popoverRef?.current?.getPopoverVisilble();
    if (account.status !==  PromiseState.PENDING) {
      handleUserActionCb && handleUserActionCb(!isActive, visible);
      if(type === ThreadDetailActionEnum.like || type === ThreadDetailActionEnum.collect) {
        if (!isLogin) {
          window.location.href = handleToUserCenter(errorCode.UNAUTHORIZED, window.location.href);
          return;
        }
        if (!isActive) {
          const lottieLoaded = lottieRef.current?.onPlay();
          if (lottieLoaded) {
            setShowAnimation(true);
          }
        }
        setCount(isActive?count-1:count+1);
        setIsActive(!isActive);
      }
    }
  }

  const callback = (visible:boolean) => {
    setIsShowToolTip(visible);
  }

  return (
    <div className={cx(Styles.root, Styles[type], className)}>
      {
        popoverNode ?
          <Popover popoverNode={popoverNode} ref={popoverRef} callback={callback}>
            <div data-tooltip={tooltipText} className={cx({"tooltip tooltipTop" : tooltipText && !isShowToolTip})}>
              <CommonIcon className={Styles.icon} icon={type} onClick={() => handleUserAction()} />
            </div>
          </Popover>
        :
        <div data-tooltip={tooltipText} className={cx({"tooltip tooltipTop" : tooltipText})}>
          <CommonIcon
            className={cx(Styles.icon, {[Styles.hide]: showAnimation})}
            icon={isActive ? `${type}-active` : type}
            onClick={() => handleUserAction()}
          />
          {
            (type === ThreadDetailActionEnum.like || type === ThreadDetailActionEnum.collect) && (
              <DotLottie
                className={cx(Styles.dotLottie, {[Styles.show]: showAnimation})}
                ref={lottieRef}
                autoplay={false}
                loop={false}
                src={type === ThreadDetailActionEnum.like ? USER_ACTION_LOTTIE_URL.PRAISE : USER_ACTION_LOTTIE_URL.COLLECT}
                onComplete={onComplete}
              />
            )
          }
        </div>
      }
      {type !== ThreadDetailActionEnum.extend && <div className={Styles.count}>{formatDisplayNumber(count)}</div>}
    </div>
  );
}
export default forwardRef(ThreadDetailAction);